import { useState } from 'react';
import { NavLink, useParams, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../hooks/useAuth';
import { useWorkspaces } from '../providers/WorkspaceProvider';

const SidebarWrapper = styled.aside<{ $isOpen: boolean }>`
  width: 260px;
  height: 100vh;
  flex-shrink: 0;
  background-color: ${({ theme }) => theme.colors.surface};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    z-index: 100;
    transform: translateX(${({ $isOpen }) => ($isOpen ? '0' : '-100%')});
    transition: transform 0.2s ease;
    box-shadow: ${({ theme }) => theme.shadows.xl};
  }
`;

const LogoArea = styled.div`
  background: linear-gradient(160deg, #0f0c29 0%, #1a1a3e 50%, #24243e 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const Logo = styled.div`
  padding: 12px 0 4px;
  display: flex;
  justify-content: center;
  img {
    height: 150px;
    width: auto;
    max-width: 90%;
    object-fit: contain;
  }
`;

const CreditsBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 4px ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.sm};
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
`;

const Nav = styled.nav`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.md};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const NavSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const NavSectionLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const NavSectionToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  background: none;
  border: none;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.borderRadius.md};

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const Chevron = styled.span<{ $open: boolean }>`
  font-size: 10px;
  transition: transform 0.15s ease;
  transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')});
`;

const StyledNavLink = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-decoration: none;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  font-size: ${({ theme }) => theme.fontSize.sm};
  transition: all 0.15s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.text};
    text-decoration: none;
  }

  &.active {
    background-color: ${({ theme }) => theme.colors.primary};
    color: white;
  }
`;

const DisabledNavItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.textMuted};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: default;
  opacity: 0.6;
`;

const NavIcon = styled.span`
  font-size: 14px;
  width: 20px;
  text-align: center;
`;

const SoonBadge = styled.span`
  font-size: 9px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.warning};
  background: rgba(245, 158, 11, 0.1);
  padding: 1px 6px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  margin-left: auto;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const BetaBadge = styled.span`
  font-size: 9px;
  font-weight: 700;
  color: #a78bfa;
  background: rgba(167, 139, 250, 0.12);
  padding: 1px 6px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  margin-left: auto;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const WorkspaceNameBanner = styled.div`
  padding: 10px ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.surfaceHover};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const WsNameText = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const WsDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const WsIcon = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
`;

const WorkspaceList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CreateWorkspaceButton = styled.button`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: 1px dashed ${({ theme }) => theme.colors.borderHover};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
    background-color: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

const Spacer = styled.div`
  flex: 1;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  margin: ${({ theme }) => theme.spacing.sm} 0;
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: transparent;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.error};
    color: white;
  }
`;

const WORKSPACE_COLORS = ['#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#06B6D4', '#F43F5E', '#6366F1'];

function getWsColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return WORKSPACE_COLORS[Math.abs(hash) % WORKSPACE_COLORS.length];
}

export function Sidebar() {
  const { user, logout } = useAuth();
  const { workspaces } = useWorkspaces();
  const [isOpen] = useState(false);
  const [workspacesOpen, setWorkspacesOpen] = useState(false);
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const isSuperAdmin = user?.role === 'super_admin';
  const isModOrAdmin = user?.role === 'mod' || user?.role === 'admin' || isSuperAdmin;
  const isInWorkspace = location.pathname.includes('/workspace/') && workspaceId;
  const currentWorkspace = workspaces.find((w) => w.id === workspaceId);

  return (
    <SidebarWrapper $isOpen={isOpen}>
      <LogoArea>
        <Logo><img src="/MainLogo1.png" alt="Agency SMM" /></Logo>
        <CreditsBar>
          <span>&#x26A1;</span>
          <span>{user?.credits ?? 0} credits</span>
        </CreditsBar>
      </LogoArea>

      {/* Workspace name banner — always visible when in a workspace */}
      {isInWorkspace && currentWorkspace && (
        <WorkspaceNameBanner>
          <WsDot $color={getWsColor(currentWorkspace.name)} />
          <WsNameText>{currentWorkspace.name}</WsNameText>
        </WorkspaceNameBanner>
      )}

      <Nav>
        {/* Quick links */}
        <NavSection>
          <StyledNavLink to="/dashboard">
            <NavIcon>📊</NavIcon> Dashboard
          </StyledNavLink>
          <StyledNavLink to="/billing">
            <NavIcon>💳</NavIcon> Billing
          </StyledNavLink>
        </NavSection>

        {/* Workspace pages — shown when inside a workspace */}
        {isInWorkspace && (
          <NavSection>
            <NavSectionLabel>Workspace</NavSectionLabel>
            <StyledNavLink to={`/workspace/${workspaceId}`} end>
              <NavIcon>🏠</NavIcon> Home
            </StyledNavLink>
            <StyledNavLink to={`/workspace/${workspaceId}/generate`}>
              <NavIcon>✨</NavIcon> Generate
            </StyledNavLink>
            <StyledNavLink to={`/workspace/${workspaceId}/brand-profile`}>
              <NavIcon>📋</NavIcon> Brand Profile
            </StyledNavLink>
            <StyledNavLink to={`/workspace/${workspaceId}/templates`}>
              <NavIcon>🎨</NavIcon> Templates
            </StyledNavLink>
            <StyledNavLink to={`/workspace/${workspaceId}/setup`}>
              <NavIcon>⚙️</NavIcon> Brand Setup
            </StyledNavLink>

            <StyledNavLink to={`/workspace/${workspaceId}/gallery`}>
              <NavIcon>🖼️</NavIcon> Gallery
            </StyledNavLink>

            <Divider />

            {/* Coming soon features */}
            <DisabledNavItem>
              <NavIcon>📅</NavIcon> Calendar <SoonBadge>Soon</SoonBadge>
            </DisabledNavItem>
            <DisabledNavItem>
              <NavIcon>👥</NavIcon> Team <SoonBadge>Soon</SoonBadge>
            </DisabledNavItem>
          </NavSection>
        )}

        {/* Admin section */}
        {isModOrAdmin && (
          <NavSection>
            <NavSectionLabel>Admin</NavSectionLabel>
            <StyledNavLink to="/mod">
              <NavIcon>📝</NavIcon> Prompts
            </StyledNavLink>
            {user?.isAdmin && (
              <StyledNavLink to="/admin">
                <NavIcon>⚙️</NavIcon> Admin Panel
              </StyledNavLink>
            )}
          </NavSection>
        )}

        {/* Beta section — super_admin only */}
        {isSuperAdmin && (
          <NavSection>
            <NavSectionLabel>Beta</NavSectionLabel>
            <StyledNavLink to="/campaigns">
              <NavIcon>🚀</NavIcon> Campaigns <BetaBadge>Beta</BetaBadge>
            </StyledNavLink>
          </NavSection>
        )}

        {/* Workspaces list */}
        <NavSection>
          <NavSectionToggle onClick={() => setWorkspacesOpen((v) => !v)}>
            All Workspaces
            <Chevron $open={workspacesOpen}>▶</Chevron>
          </NavSectionToggle>
          {workspacesOpen && (
            <WorkspaceList>
              {workspaces.map((workspace) => (
                <li key={workspace.id}>
                  <StyledNavLink
                    to={`/workspace/${workspace.id}`}
                    className={workspaceId === workspace.id ? 'active' : ''}
                  >
                    {workspace.logoUrl ? (
                      <WsIcon src={workspace.logoUrl} alt="" />
                    ) : (
                      <WsDot $color={getWsColor(workspace.name)} />
                    )}
                    {workspace.name}
                  </StyledNavLink>
                </li>
              ))}
              <li>
                <CreateWorkspaceButton onClick={() => navigate('/dashboard?create=true')}>
                  + New Workspace
                </CreateWorkspaceButton>
              </li>
            </WorkspaceList>
          )}
        </NavSection>

        <Spacer />
        <Divider />
        <LogoutButton onClick={logout}>
          <NavIcon>🚪</NavIcon> Logout
        </LogoutButton>
      </Nav>
    </SidebarWrapper>
  );
}
